<script>
  import { createEventDispatcher } from "svelte";
  import { barStyle, rowHeight, spanColor } from "./utils.js";

  export let visibleRows = [];
  export let ticks = [];
  export let vpStartNs = 0;
  export let vpDurNs = 1;
  export let selectedSpan = null;
  export let colorBy = "name";
  export let spanSearch = "";
  export let expandedPids = new Set();
  export let collapsedTreePids = new Set();
  export let rowSort = "tree";
  export let tracing_enabled;
  export let allRows = [];
  export let isPanning = false;
  export let canvasEl = null;

  export let LABEL_W = 240;
  export let LANE_H = 22;
  export let LANE_GAP = 3;
  export let ROW_PAD = 5;
  export let MAX_LANES = 6;

  const dispatch = createEventDispatcher();

  function spanDimmed(span) {
    return spanSearch.trim() && !span.name.toLowerCase().includes(spanSearch.toLowerCase());
  }
</script>

{#if allRows.length === 0}
  <div class="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-content-subtle">
    <span>No trace data.</span>
    {#if !tracing_enabled}
      <button
        class="px-3 py-1.5 text-xs font-medium rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
        on:click={() => dispatch("toggleTracing")}
      >
        Start Tracing
      </button>
    {/if}
  </div>
{:else}
  <!-- Time axis header -->
  <div class="shrink-0 flex border-b border-line-strong bg-surface-sunken">
    <div
      class="shrink-0 border-r border-line-strong"
      style="width: {LABEL_W}px; height: 26px;"
    ></div>
    <div class="flex-1 relative overflow-hidden" style="height: 26px;">
      {#each ticks as tick}
        <span
          class="absolute bottom-1 text-[10px] text-content-subtle select-none"
          style="left:{tick.pct}%;transform:translateX(-50%);"
        >
          {tick.label}
        </span>
        <span
          class="absolute bottom-0 w-px bg-surface-hover"
          style="left:{tick.pct}%;height:5px;"
        />
      {/each}
    </div>
  </div>

  <!-- Rows -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden {isPanning
      ? 'cursor-grabbing'
      : 'cursor-grab'} select-none"
    bind:this={canvasEl}
    on:wheel|nonpassive={(e) => dispatch("wheel", e)}
    on:mousedown={(e) => dispatch("startPan", e)}
    on:dblclick={(e) => dispatch("canvasDblClick", e)}
  >
    {#each visibleRows as row (row.pid)}
      {@const expanded = expandedPids.has(row.pid)}
      {@const visLanes = expanded ? row.laneCount : Math.min(row.laneCount, MAX_LANES)}
      {@const hiddenLanes = row.laneCount - visLanes}
      <div class="flex border-b border-line-subtle group hover:bg-blue-50/20">
        <!-- Label column -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="shrink-0 flex flex-col justify-center gap-0.5 py-1 border-r border-line-strong bg-surface group-hover:bg-blue-50/30 cursor-pointer"
          style="width:{LABEL_W}px;min-height:{rowHeight(
            visLanes,
            LANE_H,
            LANE_GAP,
            ROW_PAD,
          )}px;padding-left:{rowSort === 'tree'
            ? 8 + (row.depth || 0) * 16
            : 8}px;padding-right:8px;"
          on:click={() => dispatch("navigateToFirstSpan", { row })}
          title="Click to navigate to first span"
        >
          <div class="flex items-center gap-1">
            {#if rowSort === "tree" && row.depth > 0}
              <span class="text-[10px] text-gray-300 font-mono shrink-0 leading-none"
                >{row.isLastChild ? "└" : "├"}</span
              >
            {/if}
            {#if rowSort === "tree" && row.hasChildren}
              <button
                class="text-[10px] text-content-subtle hover:text-content w-3.5 shrink-0 text-center"
                on:click|stopPropagation={() => dispatch("toggleTreeCollapse", { pid: row.pid })}
                title={collapsedTreePids.has(row.pid) ? "Expand children" : "Collapse children"}
              >
                {collapsedTreePids.has(row.pid) ? "▸" : "▾"}
              </button>
            {:else if row.laneCount > MAX_LANES}
              <button
                class="text-[10px] text-content-subtle hover:text-content w-3.5 shrink-0 text-center"
                on:click|stopPropagation={() => dispatch("toggleExpand", { pid: row.pid })}
                title={expanded ? "Collapse" : "Show all lanes"}
              >
                {expanded ? "▲" : "▼"}
              </button>
            {:else}
              <span class="w-3.5 shrink-0"></span>
            {/if}
            <span
              class="text-xs font-medium text-content truncate flex-1 leading-tight"
              title={row.label}
            >
              {row.label}
            </span>
            <button
              class="text-[10px] text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 shrink-0 leading-none"
              on:click|stopPropagation={() => dispatch("toggleHide", { pid: row.pid })}
              title="Hide row"
            >
              ✕
            </button>
          </div>
          {#if row.label !== row.pid}
            <span
              class="text-[10px] text-content-subtle font-mono truncate"
              style="padding-left:{rowSort === 'tree' && row.depth > 0 ? 18 : 16}px;"
              title={row.pid}
            >
              {row.pid}
            </span>
          {/if}
          <span class="text-[10px] text-content-subtle pl-4">
            {row.spanCount} spans{row.laneCount > 1 ? ` · ${row.laneCount} lanes` : ""}
          </span>
        </div>

        <!-- Canvas -->
        <div
          class="flex-1 relative overflow-hidden"
          style="height:{rowHeight(visLanes, LANE_H, LANE_GAP, ROW_PAD)}px;"
        >
          <!-- Grid lines at ticks -->
          {#each ticks as tick}
            <span class="absolute top-0 bottom-0 w-px bg-surface-muted" style="left:{tick.pct}%;" />
          {/each}

          <!-- Spans -->
          {#each row.spans as span}
            {#if span.lane < visLanes}
              {@const style = barStyle(span, vpStartNs, vpDurNs, LANE_H, LANE_GAP, ROW_PAD)}
              {#if style}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <div
                  class="absolute rounded border overflow-hidden cursor-pointer hover:brightness-110 flex items-center transition-opacity"
                  class:ring-1={selectedSpan?.span_id === span.span_id}
                  class:ring-white={selectedSpan?.span_id === span.span_id}
                  style="{style}background-color:{spanColor(span, colorBy)
                    .bg};border-color:{spanColor(span, colorBy).border};opacity:{spanDimmed(span)
                    ? 0.2
                    : 1};"
                  on:click|stopPropagation={() => dispatch("selectSpan", { span })}
                  on:dblclick|stopPropagation={() => dispatch("zoomToSpan", { span })}
                  on:mouseenter={(e) => dispatch("showSpanTooltip", { event: e, span })}
                  on:mousemove={(e) => dispatch("moveSpanTooltip", { event: e })}
                  on:mouseleave={() => dispatch("hideSpanTooltip")}
                >
                  <span
                    class="px-1 text-[10px] text-white font-medium truncate leading-none pointer-events-none"
                  >
                    {span.name}
                  </span>
                </div>
              {/if}
            {/if}
          {/each}

          <!-- Collapsed lanes indicator -->
          {#if hiddenLanes > 0}
            <button
              class="absolute bottom-0.5 right-1 text-[9px] text-blue-500 hover:text-blue-700 bg-white/80 border border-blue-200 rounded px-1 leading-4"
              on:click|stopPropagation={() => dispatch("toggleExpand", { pid: row.pid })}
            >
              +{hiddenLanes} lane{hiddenLanes !== 1 ? "s" : ""}
            </button>
          {/if}
        </div>
      </div>
    {/each}
  </div>
{/if}
